import {
  ENTITY_REF_TYPES,
  entityRefPattern,
  MAX_ENTITY_REF_IDS,
  type EntityRefType,
} from '@orn/global/assistant/inlineMarkup.js';
import { isValidUuid } from '@orn/global/ids/uuid.js';

import type { IConversationMessage } from '../../hooks/useAssistantWebSocket';

/** The ids a transcript references, grouped by record type. */
export type EntityRefIds = Record<EntityRefType, string[]>;

// Scans raw message text rather than the parsed tree: this runs on every
// transcript change to build query variables, and re-parsing every bubble to
// markdown for it would cost far more than a regex over the same strings. The
// pattern is shared with the splitter rather than copied — a token one accepts
// and the other does not is a chip that renders permanently unresolved.

const empty = (): EntityRefIds => ({
  merchant: [],
  financial_account: [],
  transaction: [],
  bill: [],
  recurring_transaction: [],
});

/**
 * Fenced blocks and inline spans, stripped before the scan.
 *
 * The splitter leaves a token inside one literal — a reply explaining the
 * grammar shows it rather than resolving it — so collecting from there spends a
 * query on a chip that can never render. Two regexes keep this a scan over
 * strings instead of a second markdown parse of every bubble.
 */
const CODE_SPANS = /```[\s\S]*?```|~~~[\s\S]*?~~~|`[^`\n]*`/g;

/**
 * Collect every entity id the assistant referenced across a transcript, deduped
 * and in a stable order so identical inputs produce identical query variables
 * and hit the Apollo cache instead of refetching.
 *
 * Assistant turns only: a user cannot type a token that resolves, and reading
 * their text for one would let typed input drive a lookup.
 *
 * Ids that cannot address a row are dropped, and each type's list is capped at
 * {@link MAX_ENTITY_REF_IDS} — both so one bad or pathological transcript
 * cannot fail the lookup for every chip in it.
 */
export function collectEntityRefIds(messages: readonly IConversationMessage[]): EntityRefIds {
  const seen: Record<EntityRefType, Set<string>> = {
    merchant: new Set(),
    financial_account: new Set(),
    transaction: new Set(),
    bill: new Set(),
    recurring_transaction: new Set(),
  };

  for (const message of messages) {
    if (message.role !== 'assistant' || message.kind !== undefined) {
      continue;
    }
    // Replaced with a space rather than removed: deleting a span could butt the
    // text either side together into a token neither half contained.
    const prose = message.content.replace(CODE_SPANS, ' ');
    for (const match of prose.matchAll(entityRefPattern())) {
      // An id the grammar matched loosely but that cannot address a row is
      // dropped here rather than sent: the API rejects a batch holding one, so
      // asking would fail the whole lookup and leave every chip in the
      // transcript unresolved instead of just this one. Omitted, it resolves to
      // nothing and renders exactly that fallback.
      if (isValidUuid(match[2])) {
        seen[match[1] as EntityRefType].add(match[2]);
      }
    }
  }

  const out = empty();
  for (const refType of ENTITY_REF_TYPES) {
    // Sorted before the cap so the same transcript always drops the same tail,
    // which keeps the query variables stable and cache-hittable.
    out[refType] = [...seen[refType]].toSorted().slice(0, MAX_ENTITY_REF_IDS);
  }
  return out;
}

/** True when a transcript references nothing — the caller skips its query. */
export function hasNoEntityRefs(ids: EntityRefIds): boolean {
  return ENTITY_REF_TYPES.every((refType) => ids[refType].length === 0);
}
